import { loadModules } from "esri-loader";
import Vue from "vue";
import store from "../store";

export function createGraphicsLayer(options) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/GraphicsLayer"])
      .then(([GraphicsLayer]) => {
        resolve(new GraphicsLayer(options));
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function importProfileFile(fileName) {
  return new Promise((resolve, reject) => {
    import(/* webpackIgnore: true */ process.env.VUE_APP_CONFIG_FILES_URL +
      "tema/" +
      fileName.toLowerCase() +
      ".js?version=" +
      new Date().getTime())
      .then((file) => {
        resolve(file);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function importBookmarksFile() {
  return new Promise((resolve, reject) => {
    import(/* webpackIgnore: true */ process.env.VUE_APP_CONFIG_FILES_URL +
      "bookmarks.js?version=" +
      new Date().getTime())
      .then((file) => {
        resolve(file);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function getLayerByID(id) {
  return Vue.prototype.$theMapObject.findLayerById(id);
}

export function removeAllGraphicsExceptLocationGraphic(view) {
  let graphicsToRemove = [];
  view.graphics.items.forEach((graphic) => {
    if (
      graphic.hasOwnProperty("attributes") &&
      graphic.attributes.hasOwnProperty("altitudeAccuracy")
    ) {
      return;
    } else {
      graphicsToRemove.push(graphic);
    }
  });
  view.graphics.removeMany(graphicsToRemove);
}

export function switchMapTo3d(cameraOptions) {
  Vue.prototype.$eventHub.$emit("switch-to-3d", cameraOptions);
}

export function switchMapTo2d() {
  Vue.prototype.$eventHub.$emit("switch-to-2d");
}

export function getSavedMapDefinition(savedMap) {
  return JSON.parse(savedMap.map.attributes.JSON);
}

export function // Profiles
mergeProfileOptions(profile, payload) {
  let optionsToBeMerged = {
    canBeDownloaded: false,
    visible: true,
    opacity: 1.0,
    open: true,
    type: "mapImageLayer",
    ShowAtStartUpLayers: [],
  };
  profile = mergeOptions(optionsToBeMerged, profile, payload);

  if (payload.hasOwnProperty("date")) {
    profile.date = payload.date;
  }

  profile.ShowAtStartupLayers = payload.hasOwnProperty("showAtStartupLayers")
    ? payload.showAtStartupLayers
    : profile.hasOwnProperty("ShowAtStartUpLayers")
    ? profile.ShowAtStartUpLayers
    : [];

  profile.imageAttribution = store.state.profiles[profile.id].imageAttribution;

  return profile;
}

export function getLayerGraphicsAsJSON(graphicsLayerID) {
  let graphicsLayer = getLayerByID(graphicsLayerID);
  let jsonArray = [];
  graphicsLayer.graphics.items.forEach((graphic) => {
    jsonArray.push(graphic.toJSON());
  });
  return jsonArray;
}

export function getDuplicateLayerProfile(id, title, description) {
  return {
    id: id,
    open: false,
    icon: "edit",
    visible: true,
    appTitle: title + " kopi",
    Temaforklaring: description,
    type: "graphicsLayer",
  };
}

export function getUniqueLayerID() {
  return new Date().getTime().toString();
}

export function addLayerToMap(layer) {
  Vue.prototype.$theMapObject.add(layer);
}

export async function getArcGISLayerInfo(url) {
  return executeEsriRequest(url + "?f=pjson", {
    responseType: "json",
  });
}

export function getFieldTypeFromLayerInfo(fieldName, fieldsInfo) {
  return fieldsInfo.filter((field) => {
    return field.name.toUpperCase() === fieldName.toUpperCase();
  })[0].type;
}

export function getFieldValueStringFromLayerInfo(
  fieldName,
  fieldValue,
  fieldsInfo
) {
  let fieldType = getFieldTypeFromLayerInfo(fieldName, fieldsInfo);

  let formattedFieldValueString =
    fieldType === "esriFieldTypeString" ? "'" + fieldValue + "'" : fieldValue;

  return formattedFieldValueString;
}

export function queryArcGISLayer(url, queryOptions) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/tasks/QueryTask", "esri/tasks/support/Query"]).then(
      ([QueryTask, Query]) => {
        let queryTask = new QueryTask({
          url: url,
        });

        let query = new Query(queryOptions);

        queryTask
          .execute(query)
          .then((results) => {
            resolve(results);
          })
          .catch((error) => {
            reject(error);
          });
      }
    );
  });
}

export function getFirstValidExtentFromURLQuery(urlQuery) {
  return new Promise((resolve, reject) => {
    let layers = urlQuery.layer.split(",");
    for (let i = 0; i < layers.length; i++) {
      getArcGISLayerInfo(urlQuery.url + "/" + layers[i])
        .then((layerInfo) => {
          getExtentFromArcGISLayerBasedOnURLQuery(
            urlQuery,
            layerInfo,
            layers[i]
          ).then((extent) => {
            if (extent) {
              resolve(extent);
            }
          });
        })
        .catch((error) => {
          reject(error);
        });
    }
  });
}

export function getExtentFromArcGISLayerBasedOnURLQuery(
  urlQuery,
  layerInfo,
  layerID
) {
  return new Promise((resolve, reject) => {
    let fieldValueString = getFieldValueStringFromLayerInfo(
      urlQuery.field,
      urlQuery.value,
      layerInfo.data.fields
    );

    let queryOptions = {
      returnGeometry: true,
      outFields: ["*"],
      where: urlQuery.field + " = " + fieldValueString,
    };

    queryArcGISLayer(urlQuery.url + "/" + layerID, queryOptions)
      .then((results) => {
        if (results.features.length > 0) {
          getExtentFromFeature(results.features[0]).then((extent) => {
            resolve(extent);
          });
        } else {
          resolve(false);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function getExtentFromFeature(feature) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/geometry/Extent"])
      .then(([Extent]) => {
        let geometry = feature.geometry;

        if (geometry.type === "point") {
          resolve(
            new Extent({
              xmin: geometry.x,
              xmax: geometry.x,
              ymin: geometry.y,
              ymax: geometry.y,
              spatialReference: {
                wkid: 25833,
              },
            })
          );
        } else {
          resolve(feature.geometry.extent);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function placePhotoLayerOnTopIfActive() {
  let photolayer = Vue.prototype.$theMapObject.findLayerById("foto");
  if (photolayer) {
    reorderMapLayer(photolayer, 1000);
  }
}

export function reorderMapLayer(layer, index) {
  Vue.prototype.$theMapObject.reorder(layer, index);
}

export function mergeOptions(optionsToBeMerged, firstObject, secondObject) {
  for (let k in optionsToBeMerged) {
    let defaultValue = optionsToBeMerged[k];
    firstObject[k] = mergeOption(firstObject[k], secondObject[k], defaultValue);
  }
  return firstObject;
}

export function mergeOption(value1, value2, defaultValue) {
  if (typeof value2 !== "undefined") {
    return value2;
  }
  if (typeof value1 !== "undefined") {
    return value1;
  }

  return defaultValue;
}

export function cloneObject(theObject) {
  return Object.assign({}, theObject);
}

export function createMapLayer(options, profile) {
  switch (options.type) {
    case "imageryLayer":
      return createImageryLayer(profile);
    case "wmsLayer":
      return createWMSLayer(profile);
    case "sceneLayer":
      return createSceneLayer(profile);
    case "featureLayer":
      return createFeatureLayer(profile, options);
    case "mapImageLayer":
      return createMapImageLayer(profile);
    case "shapeLayer":
      return createShapeLayer(profile);
    default:
      return createMapImageLayer(profile);
  }
}

export function createImageryLayer(profile) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/ImageryLayer", "esri/layers/support/MosaicRule"])
      .then(([ImageryLayer, MosaicRule]) => {
        let layer = new ImageryLayer({
          id: profile.id,
          url: profile.serviceUrl,
          visible: profile.visible,
          opacity: profile.opacity ? profile.opacity : 1.0,
        });
        if (profile.hasOwnProperty("date")) {
          let mosaicRule = new MosaicRule();
          mosaicRule.method = "none";
          mosaicRule.ascending = false;
          mosaicRule.where =
            "opptakstidspunkt >= date '" +
            profile.date +
            " 00:00:00' AND opptakstidspunkt <= date '" +
            profile.date +
            " 23:59:59'";
          layer.mosaicRule = mosaicRule;
        }
        resolve(layer);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function createWMSLayer(profile) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/WMSLayer"])
      .then(([WMSLayer]) => {
        let layer = new WMSLayer({
          id: profile.id,
          visible: profile.visible,
          url: profile.serviceUrl,
          sublayers: profile.sublayers,
          opacity: profile.opacity ? profile.opacity : 1.0,
        });
        resolve(layer);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function createSceneLayer(profile) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/SceneLayer", "esri/layers/GroupLayer"])
      .then(([SceneLayer, GroupLayer]) => {
        let layers = [];
        profile.VisibleLayers.forEach((layerId) => {
          let layer = new SceneLayer({
            id: profile.id + layerId,
            url: profile.serviceUrl + "/" + layerId,
            popupEnabled: false,
            // minScale: 8000
          });
          layers.push(layer);
        });
        let groupLayer = new GroupLayer({
          id: profile.id,
          layers: layers,
          opacity: profile.opacity ? profile.opacity : 1.0,
        });
        resolve(groupLayer);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function createFeatureLayer(profile, options) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/FeatureLayer"])
      .then(([FeatureLayer]) => {
        let currentFeatureLayerOptions =
          options.isConnectedToIntranet && // HER
          profile.hasOwnProperty("intranetFeatureLayerOptions")
            ? profile.intranetFeatureLayerOptions
            : profile.featureLayerOptions;

        let featureLayerOptionsCopy = JSON.parse(
          JSON.stringify(currentFeatureLayerOptions)
        );
        // HER
        if (
          options.is3d &&
          featureLayerOptionsCopy.hasOwnProperty("renderer3d")
        ) {
          featureLayerOptionsCopy.renderer = featureLayerOptionsCopy.renderer3d;
        }
        let layer = new FeatureLayer(featureLayerOptionsCopy);
        layer.id = profile.id;
        resolve(layer);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export async function createMapImageLayer(profile) {
  return new Promise((resolve, reject) => {
    loadModules(["esri/layers/MapImageLayer"]).then(
      async ([MapImageLayer]) => {
        let sublayers = await getMapImageSublayers(profile);

        let layer = new MapImageLayer(
          getCreateMapImageLayerProperties(profile, sublayers)
        );
        let legendResponse = await getArcGISLayerLegendInfo(profile.serviceUrl);
        addLegendDataToSublayers(
          layer.sublayers.items,
          legendResponse.data.layers
        );

        resolve(layer);
      },
      function(error) {
        reject(error);
      }
    );
  });
}

export function createShapeLayer(profile) {
  return new Promise((resolve) => {
    resolve(profile.featureLayer);
  });
}

export async function getHydratedGraphics(graphicsAsJson) {
  let hydratedGraphics = [];
  for (let i = 0; i < graphicsAsJson.length; i++) {
    let graphicJSON = graphicsAsJson[i];
    let graphic = await hydrateGraphicFromJSON(graphicJSON);
    hydratedGraphics.push(graphic);
  }

  return hydratedGraphics;
}

export function getGraphicslayerMapProfile(options) {
  return {
    id: options.id.toString(),
    open: options.open,
    icon: "edit",
    visible: options.visible,
    appTitle: options.appTitle,
    Temaforklaring: options.Temaforklaring,
    type: "graphicsLayer",
  };
}

export async function hydrateGraphicFromJSON(graphicJSON) {
  let hydratedGraphic = await getGraphicFromJSON(graphicJSON);
  hydratedGraphic.popupTemplate = createGraphicPopupTemplate(hydratedGraphic);

  return hydratedGraphic;
}

export function createGraphicPopupTemplate(graphic) {
  if (graphic.hasOwnProperty("attributes")) {
    let popupTemplateContentString = "<table><tbody>";

    for (let attributeKey in graphic.attributes) {
      popupTemplateContentString = popupTemplateContentString.concat(
        "<tr><td style='vertical-align:top'><b>" +
          attributeKey +
          "</b></td><td>&nbsp;" +
          graphic.attributes[attributeKey] +
          "</td></tr>"
      );
    }

    popupTemplateContentString = popupTemplateContentString.concat(
      "<table><tbody>"
    );

    return {
      title: graphic.attributes.name ? graphic.attributes.name : "Uten navn",
      content: popupTemplateContentString,
    };
  } else {
    return null;
  }
}

export async function getGraphicFromJSON(graphicJSON) {
  const [Graphic] = await loadModules(["esri/Graphic"]);
  return Graphic.fromJSON(graphicJSON);
}

export async function executeEsriRequest(url, options) {
  const [esriRequest] = await loadModules(["esri/request"]);
  let result = await esriRequest(url, options);
  return result;
}

export async function getArcGISLayerLegendInfo(url) {
  return executeEsriRequest(url + "/legend?f=pjson", {
    responseType: "json",
  });
}

export function getCreateMapImageLayerProperties(profile, sublayers) {
  return {
    url: profile.serviceUrl,
    id: profile.id,
    imageFormat: "png32",
    title: profile.appTitle,
    sublayers: sublayers,
    opacity: profile.opacity ? profile.opacity : 1.0,
    visible: profile.visible,
  };
}

export async function getSublayerRendererFromJSON(rendererAsJSON) {
  loadModules(["esri/renderers/support/jsonUtils"]).then(
    async ([jsonUtils]) => {
      return jsonUtils.fromJSON(rendererAsJSON);
    }
  );
}

export async function getMapImageSublayers(profile) {
  let infoResponse = await getArcGISLayerInfo(profile.serviceUrl);

  let sublayers = [];

  if (profile.sublayers) {
    sublayers = profile.sublayers;
    sublayers[0].renderer = await getSublayerRendererFromJSON(
      sublayers[0].renderer
    );
  } else {
    let visibleSublayersOnly = filterVisibleSublayers(
      infoResponse.data.layers,
      profile.VisibleLayers
    );

    setVisibilityOfSublayers(
      visibleSublayersOnly,
      profile.ShowAtStartUpLayers,
      profile.setParentLayerVisibleIfSublayersAreVisibleAtStartup,
      profile.visibleLayersOpacity
    );

    sublayers = createLayerHierarchyFromFlatLayerJSON(visibleSublayersOnly);
  }

  sublayers = reverseSublayers(sublayers);
  sublayers = deleteSublayersKeyFromSublayerIfEmpty(sublayers);
  return sublayers;
}

export function reverseSublayers(sublayers) {
  sublayers.forEach((sublayer) => {
    if (sublayer.hasOwnProperty("sublayers")) {
      if (sublayer.sublayers.length > 0) {
        sublayer.sublayers = reverseSublayers(sublayer.sublayers);
      }
    }
  });
  return sublayers.slice().reverse();
}

export function deleteSublayersKeyFromSublayerIfEmpty(sublayers) {
  sublayers.forEach((sublayer) => {
    if (sublayer.hasOwnProperty("sublayers")) {
      if (sublayer.sublayers.length > 0) {
        sublayer.sublayers = deleteSublayersKeyFromSublayerIfEmpty(
          sublayer.sublayers
        );
      } else {
        delete sublayer.sublayers;
      }
    }
  });
  return sublayers;
}

export function filterVisibleSublayers(layerSourceJSON, visibleLayerIndexes) {
  return layerSourceJSON.filter((item) => {
    return visibleLayerIndexes.includes(item.id);
  });
}

export function setVisibilityOfSublayers(
  sublayers,
  showAtStartup,
  setParentLayerVisibleIfSublayersAreVisibleAtStartup,
  visibleLayersOpacity = []
) {
  for (let i = sublayers.length - 1; i >= 0; i--) {
    let layerJSON = sublayers[i];

    if (showAtStartup.includes(layerJSON.id)) {
      layerJSON.visible = true;
    } else if (
      setParentLayerVisibleIfSublayersAreVisibleAtStartup &&
      layerJSON.subLayerIds !== null &&
      showAtStartup.some(
        (visibleLayerIndex) =>
          layerJSON.subLayerIds.indexOf(visibleLayerIndex) >= 0
      )
    ) {
      layerJSON.visible = true;
    } else {
      layerJSON.visible = false;
    }
    if (
      visibleLayersOpacity.some(
        (opacityInfo) => opacityInfo.layer === layerJSON.id
      )
    ) {
      layerJSON.opacity = visibleLayersOpacity.find(
        (opacityInfo) => opacityInfo.layer === layerJSON.id
      ).opacity;
    }
  }
}

export function addLegendDataToSublayers(sublayers, legendData) {
  for (let i = sublayers.length - 1; i >= 0; i--) {
    let layerJSON = sublayers[i];

    if (legendData.some((legendInfo) => legendInfo.layerId === layerJSON.id)) {
      let layerLegendInfo = legendData.find(
        (legendSource) => legendSource.layerId === layerJSON.id
      );
      layerJSON.legendImages = layerLegendInfo.legend;
      layerJSON.title = layerLegendInfo.layerName;
    } else {
      if (
        layerJSON.hasOwnProperty("sublayers") &&
        layerJSON.sublayers.items.length > 0
      ) {
        addLegendDataToSublayers(layerJSON.sublayers.items, legendData);
      }
      layerJSON.legendImages = [];
    }
  }
}

export function createLayerHierarchyFromFlatLayerJSON(
  items,
  id = -1,
  link = "parentLayerId"
) {
  return items
    .filter((item) => item[link] === id)
    .map((item) => ({
      ...item,
      sublayers: createLayerHierarchyFromFlatLayerJSON(items, item.id),
    }));
}
